import React, { useContext, useEffect, useState } from 'react';
import { CREATE, CREATE_ISR, EMPTY_ISR, ISR_NAME, PROMOTE_DATA } from '../ISRConstants';
import HorizontalLine from 'Components/Common/components/Button/HorizontalLine';
import { useHistory, useLocation } from 'react-router-dom';
import NoDataMessage from '../Components/NoDataMessage';
import CreateISRDialog from '../Components/CreateISRDialog';
import ISRProjectTable from './ISRProjectTable';
import { createProject, deleteISR, getISRProjects } from './DefineModel/ModelServices/ModalServices';
import TableSkeletonElement from 'Components/GMS/Products/components/TableSkeletonElement';
import { TISRProjects, TUpsertISRProjectDataModelsInput1 } from './DefineModel/model';
import Paginator from 'Components/Common/PaginatorWithApi';
import { TPaginationParams } from 'Pages/GMS/Performance/analytics';
import DeleteProjectModal from '../Components/DeleteProjectModal';
import { toast } from 'react-toastify';
import { useISRContext } from '../Hooks/useISRContext';
import { AppContext } from 'Context/AppContext';
import { GearIcon } from "../../../../assests/GmsIcons";
import MerchantSettings from "./MerchantSettings";
import PromotionDialog from "../Components/PromotionDialog";
import { Dropdown } from './Dropdown';
import { useProductModelDownload } from './useProductModelDownload';
import { UploadModal } from './UploadModal';

const ISRHomePage = () => {
    const history = useHistory();
    const location = useLocation();
    const isrContext = useISRContext();
    const [isrProjects, setIsrProjects] = useState<TISRProjects>([]);
    const [totalRecords, setTotalRecords] = useState<number>(0);

    const isGuide = Boolean(location.pathname.includes('/guide'));

    const appData = useContext(AppContext);
    const readPermission = isGuide ? appData.pm.checkPermission("gmsguide_read") : appData.pm.checkPermission("smartbuilderthree_read");
    const updatePermission = isGuide ? appData.pm.checkPermission("gmsguide_update") : appData.pm.checkPermission("smartbuilderthree_update");
    const mappingOverridePermission = appData.pm.checkPermission("gmsguide_override");


    const [deleteModal, setDeleteModal] = useState<boolean>(false);
    const [deleteLoading, setDeleteLoading] = useState<boolean>(false);
    const [deleteItem, setDeleteItem] = useState<{ id: string, name: string }>({ id: '', name: '' });

    const [merchantSettings, setMerchantSettings] = useState<any>([]);
    const [merchantSettingsLoading, setMerchantSettingsLoading] = useState<boolean>(true);
    const [showDialog, setShowDialog] = useState<boolean>(false);
    const [showLoader, setShowLoader] = useState<boolean>(false);
    const [loader, setLoader] = useState<boolean>(false);
    const [showMerchantSettings, setShowMerchantSettings] = useState<boolean>(false);
    const [showPromotionDialog, setShowPromotionDialog] = useState<boolean>(false);
    const [showUploadModal, setShowUploadModal] = useState(false);
    const { handleDownload, downloadUrl } = useProductModelDownload();

    const openDialog = () => setShowDialog(true);
    const closeDialog = () => setShowDialog(false);

    const navigateToMainPage = (name: string, id: string) => {

        if (isGuide) {
            history.push(`/gms/guide/main/${name}/${id}`);
        } else {
            history.push(`/gms/isr/main/${name}/${id}`);
        }
    }

    const onFieldSubmit = (fieldName: string) => {
        if (!updatePermission) {
            toast.warn('You do not have permission to create ISR project');
            return;
        }
        const isrProject: TUpsertISRProjectDataModelsInput1 = { industry: isrContext?.industryId ?? '', channel: appData?.channelKey, id: '', project: { ISRName: fieldName, dataModels: [], fields: [], productCategories: [], connectors: [], id: '' }, rules: [] };
        createISRProject(isrProject);
    }

    useEffect(() => {
        if (!readPermission) {
            history.push("/workspace/module/permission/denied");
            return;
        }
        if (appData?.channelKey && isrContext?.industryId) {
            isrContext?.clearClassData();
            fetchMerchantConfig();
        }
    }, [isrContext?.industryId, appData?.channelKey, isGuide]);

    useEffect(() => {
        if (!readPermission) {
            return;
        }
        if (appData?.channelKey && isrContext?.industryId) {
            fetchISRProjects();
        }
    }, [isrContext?.paginationParams, isrContext?.industryId, appData?.channelKey, isGuide]);


    const handlePageChange = (pageNumber: number) => {
        isrContext?.setPaginationParams((prevState: TPaginationParams) => ({
            ...prevState,
            pageNumber: pageNumber,
        }));
    }

    const updatePageSize = (pageSize: number) => {
        isrContext?.setPaginationParams((prevState: TPaginationParams) => ({
            ...prevState,
            pageSize: pageSize,
            pageNumber: 1
        }));
    };

    const createISRProject = async (isrProject: TUpsertISRProjectDataModelsInput1) => {
        try {
            setLoader(true);
            const response = await createProject(isrProject, isGuide);
            if (response === null) return;
            if (response?.done) {
                toast.success(response?.message ?? '');
                const projectName = response?.project_data?.project?.ISRName ?? '';
                const projectId = response?.project_data?.id ?? '';
                navigateToMainPage(projectName, projectId);
                setShowDialog(false);
            } else {
                toast.error(response?.message ?? '');
            }
        } catch (e) {
            console.log('Error', e);
        } finally {
            setLoader(false);
        }
    }

    const fetchISRProjects = async () => {
        setShowLoader(true);
        try {
            const responseData = await getISRProjects(isGuide, isrContext?.industryId ?? '', appData?.channelKey, isrContext?.paginationParams);
            if (responseData?.done) {
                setTotalRecords(responseData?.total ?? 10);
                setIsrProjects(responseData?.data ?? []);
            } else {
                setIsrProjects([]);
            }
        } catch (error) {
            setIsrProjects([]);
            console.log(error, 'Error');
        } finally {
            setShowLoader(false);
        }
    }

    const fetchMerchantConfig = async () => {
        if (!appData?.channelKey) {
            setMerchantSettingsLoading(false);
            return;
        }
        try {
            setMerchantSettingsLoading(true);
            const responseData = await isrContext?.getMerchantConfig(appData?.channelKey ?? '');
            if (responseData?.done === true) {
                setMerchantSettings(responseData.data.sb3_channel_config ?? []);
            } else {
                setMerchantSettings([]);
            }
        } catch (e) {
            setMerchantSettings([]);
        } finally {
            setMerchantSettingsLoading(false);
        }
    }

    const closeDeleteModal = () => {
        setDeleteModal(false);
    };

    const handleDelete = async () => {
        if (!deleteItem?.id) {
            toast.info('Project id is missing');
            return;
        }
        setDeleteLoading(true);
        try {
            const responseData = await deleteISR(deleteItem?.id);
            if (responseData?.done) {
                toast.success(responseData?.message);
                isrContext?.setPaginationParams({ pageNumber: 1, pageSize: 10 });
            } else {
                toast.error(responseData?.message);
            }
        } catch (error) {
            console.log('Error', error);
        } finally {
            setDeleteLoading(false);
            setDeleteModal(false);
        }
    }

    const handlePromoteData = async (categories: []) => {
        setShowPromotionDialog(false);
        if (!isrContext?.industryId) return;
        try {
            isrContext?.setDisableButton(true);
            await isrContext?.promoteProducts(isrContext?.industryId, categories);
        } catch (e) {
            console.log(e);
        } finally {
            isrContext?.setDisableButton(false);
        }
    }

    const updateMerchantConfig = async (merchantConfigData: []) => {
        if (!isrContext?.industryId) return;
        try {
            const updatedValues = merchantConfigData.map((e: any) => ({
                config_attr: e.config_attr,
                value: e.value
            }));
            const responseData = await isrContext?.updateMerchantConfig(appData?.channelKey ?? '', updatedValues);
            if (responseData?.done) {
                setMerchantSettings(merchantConfigData);
                toast.success('Merchant settings updated successfully');
            }
        } catch (e) {
            console.log(e);
        }
    }

    const channelKey = appData?.channelKey;

    const dropdownOptions = [
        {
            label: 'Upload',
            value: 'upload',
            onClick: () => setShowUploadModal(true)
        },
        {
            label: 'Download',
            value: 'download',
            onClick: () => channelKey ? handleDownload(channelKey) : null
        }
    ];

    return (
        <React.Fragment>
            <div id="isr-header" className={`tw-pt-[12px] tw-flex tw-mb-1`}>
                <label id="isr-title" className={`tw-text-black tw-text-xl tw-font-semibold tw-pe-4`}>{isGuide ? 'Guide' : 'Guide'}
                </label>
                {!merchantSettingsLoading && merchantSettings.length > 0 && (
                    <button id="merchant-settings-btn" className={`tw-rounded tw-bg-shades-of-grey-0 tw-text-primary tw-flex tw-flex-row tw-items-center tw-justify-center tw-h-7 tw-py-1 tw-px-2.5 tw-gap-[4px] tw-text-sm  tw-border-[1px] tw-border-solid tw-border-primary`} onClick={() => { setShowMerchantSettings(!showMerchantSettings); }} >
                        <GearIcon />
                        {'Merchant Settings'}
                    </button>
                )}
                <div className='tw-flex tw-flex-grow' />
                {mappingOverridePermission && <Dropdown
                    id="product-model-override"
                    label="Product-model override"
                    options={dropdownOptions}
                    className='tw-mx-2'
                />}
                <UploadModal
                    id={`product-upload-modal`}
                    showModal={showUploadModal}
                    closeModal={() => setShowUploadModal(false)}
                    channelKey={channelKey}
                />
                {/* Hidden iframe for download */}
                <iframe
                    style={{ display: 'none' }}
                    src={downloadUrl || undefined}
                    id="iframe_product_model_download"
                    title="product_model_download"
                    data-testid={`download-iframe`}
                />
                <div id="header-actions" className='tw-flex tw-justify-end tw-items-center tw-gap-2 tw-bg-red'>
                    <button
                        id="promote-data-btn"
                        disabled={isrContext?.disableButton}
                        className={`button-style tw-w-max`}
                        onClick={() => setShowPromotionDialog(true)}>
                        {PROMOTE_DATA}
                    </button>
                    <button
                        id="create-isr-btn"
                        className={`button-style tw-w-max`}
                        onClick={openDialog}>
                        {CREATE}
                    </button>
                </div>
            </div>
            <HorizontalLine />
            {!showLoader && isrProjects.length === 0 ?
                <div id="empty-state" className='tw-flex tw-justify-center tw-mt-20'>
                    <NoDataMessage
                        id="isr-no-data"
                        buttonText={CREATE}
                        message={EMPTY_ISR}
                        buttonAction={openDialog}
                        width='' />
                </div> : null}
            {showLoader ? <TableSkeletonElement /> : null}
            {!showLoader && isrProjects?.length > 0 ?
                <ISRProjectTable
                    isrProjects={isrProjects}
                    setDeleteModal={setDeleteModal}
                    setDeleteItem={setDeleteItem}
                    isGuide={isGuide}
                    updatePermission={updatePermission}
                    fetchISRProjects={fetchISRProjects}
                /> : null}
            {!showLoader && isrProjects?.length > 0 ?
                <div id="pagination-container" className="tw-py-2">
                    <div className={`tw-flex tw-justify-end tw-items-center tw-space-x-3 tw-flex-wrap`}>
                        <div className={`tw-flex tw-items-center tw-space-x-3`}>
                            <span>Show</span>
                            <select
                                id="page-size-select"
                                className={`tw-border focus:tw-outline-none tw-p-1`}
                                style={{ width: "80px" }}
                                aria-label="Records on page select"
                                value={isrContext?.paginationParams?.pageSize ?? 10}
                                onChange={(e) => {
                                    updatePageSize(parseInt(e?.target?.value));
                                }}
                            >
                                <option value={10}>10</option>
                                <option value={20}>20</option>
                            </select>
                        </div>
                        <Paginator
                            onPageChange={handlePageChange}
                            recordPerPage={isrContext?.paginationParams?.pageSize}
                            presentPage={isrContext?.paginationParams?.pageNumber}
                            totalRecords={totalRecords}
                        />
                    </div>
                </div> : null}
            {showDialog ?
                <CreateISRDialog
                    id={`create-isr`}
                    dialogHeader={CREATE_ISR}
                    labelName={ISR_NAME}
                    onClose={closeDialog}
                    onSubmodelFieldSubmit={onFieldSubmit}
                    showIndicator={loader}
                /> : null}
            {showMerchantSettings ?
                <MerchantSettings
                    merchantSettings={merchantSettings}
                    onClose={() => setShowMerchantSettings(false)}
                    onSubmit={async (merchantConfigData: []) => {
                        setShowMerchantSettings(false);
                        await updateMerchantConfig(merchantConfigData);
                    }}
                /> : null}
            {showPromotionDialog ?
                <PromotionDialog
                    onClose={() => setShowPromotionDialog(false)}
                    onFieldSubmit={(categories: []) => handlePromoteData(categories)}
                /> : null}
            <DeleteProjectModal
                showModal={deleteModal}
                closeModal={closeDeleteModal}
                loading={deleteLoading}
                onConfirm={handleDelete}
                deleteItem={deleteItem}
            />
        </React.Fragment>
    );
}

export default ISRHomePage;